/* eslint-disable @typescript-eslint/restrict-template-expressions */
import {
	IAnalyticsEvent,
	IAnalyticsTargetedEventsTracker,
	IErrorLogger,
} from '@verv-inc/js-toolkit';
import { IWorkoutsPreferencesAnswer } from 'modules/survey/components/customQuestions/workoutPreferences';
import { IQuestion } from 'modules/survey/interfaces';
import { EVENT_TRACKERS } from 'utils/analytics';
import {
	GTMOnbordingQuestionEvent,
	NewGTMOnbordingQuestionEvent,
} from 'utils/analytics/events/GTMOnbordingQuestionEvents';
import { IHeightAnswer, IWeightAnswer } from './unitsHelpers';

enum CUSTOM_SEND_EVENT_QUESTION {
	WORKOUT_PREFERENCES = 'workouts_preferences',
	WEIGHT = 'weight',
	HEIGHT = 'height',
	TARGET_WEIGHT = 'target_weight',
	LOADER = 'loader',
}

const customSendEventQuestionAnswer = async (
	question: IQuestion,
	eventsTracker: IAnalyticsTargetedEventsTracker,
	errorLogger: IErrorLogger,
): Promise<void> => {
	const events: IAnalyticsEvent[] = [];

	// send own event for every WORKOUT_PREFERENCES answer
	if (question.name === CUSTOM_SEND_EVENT_QUESTION.WORKOUT_PREFERENCES) {
		const jsonString = question.answer?.join();

		if (jsonString) {
			try {
				const answer: IWorkoutsPreferencesAnswer = JSON.parse(jsonString);
				const videoAnswers = answer.videoAnswers;

				for (const key in videoAnswers) {
					const value = videoAnswers[key];

					const event = GTMOnbordingQuestionEvent.answerQuestion({
						question: key,
						answer: [value],
					});
					const newEvent = NewGTMOnbordingQuestionEvent.answerQuestion({
						question: key,
						answer: [value],
					});
					events.push(event);
					events.push(newEvent);
				}
			} catch (error) {
				errorLogger.logError(error as Error);
			}
		}
	}

	// send own event for every WEIGHT answers
	if (
		question.name === CUSTOM_SEND_EVENT_QUESTION.WEIGHT ||
		question.name === CUSTOM_SEND_EVENT_QUESTION.TARGET_WEIGHT
	) {
		const jsonString = question.answer?.join();

		if (jsonString) {
			try {
				const answer: IWeightAnswer = JSON.parse(jsonString);
				const { unit, value } = answer;

				const event = GTMOnbordingQuestionEvent.answerQuestion({
					question: question.name,
					answer: [`${value ?? '0'} ${unit ?? 'lbs'}`],
				});
				const newEvent = NewGTMOnbordingQuestionEvent.answerQuestion({
					question: question.newAnalyticsName || question.name,
					answer: [`${value ?? '0'} ${unit ?? 'lbs'}`],
				});
				events.push(event);
				events.push(newEvent);
			} catch (error) {
				errorLogger.logError(error as Error);
			}
		}
	}
	// send own event for every HEIGHT answers
	if (question.name === CUSTOM_SEND_EVENT_QUESTION.HEIGHT) {
		const jsonString = question.answer?.join();

		if (jsonString) {
			try {
				const answer: IHeightAnswer = JSON.parse(jsonString);
				const { value } = answer;

				const eventAnswer = value?.[1]?.value
					? [
							`${value?.[0].value ?? 'cm'} ${value?.[0]?.unit ?? '0'} ${value?.[1].value ?? 'cm'} ${
								value?.[1]?.unit ?? '0'
							}`,
					  ]
					: [`${value?.[0].value ?? 'cm'} ${value?.[0]?.unit ?? '0'}`];

				const event = GTMOnbordingQuestionEvent.answerQuestion({
					question: question.name,
					answer: eventAnswer,
				});
				const newEvent = NewGTMOnbordingQuestionEvent.answerQuestion({
					question: question.newAnalyticsName || question.name,
					answer: eventAnswer,
				});
				events.push(event);
				events.push(newEvent);
			} catch (error) {
				errorLogger.logError(error as Error);
			}
		}
	}
	// send own event for every LOADER answers
	if (question.name === CUSTOM_SEND_EVENT_QUESTION.LOADER) {
		const newEvent = NewGTMOnbordingQuestionEvent.answerQuestion({
			question: question.newAnalyticsName || question.name,
			answer: 'continueAuto',
		});
		events.push(newEvent);
	}

	for (const event of events) {
		await eventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM).catch(errorLogger.logError);
	}
};

export const sendEventQuestionAnswer = async (
	question: IQuestion,
	eventsTracker: IAnalyticsTargetedEventsTracker,
	errorLogger: IErrorLogger,
): Promise<void> => {
	if (
		Object.values(CUSTOM_SEND_EVENT_QUESTION).includes(question.name as CUSTOM_SEND_EVENT_QUESTION)
	) {
		return await customSendEventQuestionAnswer(question, eventsTracker, errorLogger);
	}

	const questionAnalyticsName: string = question.analyticsName ?? question.name;
	const newQuestionAnalyticsName: string = question.newAnalyticsName ?? question.name;

	const event = GTMOnbordingQuestionEvent.answerQuestion({
		question: questionAnalyticsName,
		answer: question.answer,
	});

	const newEvent = NewGTMOnbordingQuestionEvent.answerQuestion({
		question: newQuestionAnalyticsName,
		answer: question.answer,
	});

	await eventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM).catch(errorLogger.logError);
	if (question.name !== 'intermittent_fasting') {
		await eventsTracker.trackWithTarget(newEvent, EVENT_TRACKERS.GTM).catch(errorLogger.logError);
	}
};
